import styled from "styled-components";

// Small building blocks for the engine's guest page: phone first, big tap
// targets, the website's teal and orange.

export const Page = styled.main`
	background: ${({ theme }) => theme.colors.background};
	display: flex;
	justify-content: center;
	min-height: 100vh;
	padding-bottom: 96px;
`;

export const Panel = styled.section`
	background: #ffffff;
	max-width: 640px;
	padding: 20px 16px;
	width: 100%;

	@media (min-width: ${({ theme }) => theme.breakpoints.md}) {
		border-radius: 12px;
		box-shadow: 0 16px 48px rgba(8, 47, 58, 0.12);
		margin-top: 24px;
		padding: 28px;
	}
`;

export const Eyebrow = styled.p`
	color: ${({ theme }) => theme.colors.primary};
	font-size: 0.78rem;
	font-weight: 900;
	letter-spacing: 0.08em;
	margin: 0 0 6px;
	text-transform: uppercase;
`;

export const Title = styled.h1`
	font-size: clamp(1.7rem, 7vw, 2.4rem);
	line-height: 1.1;
	margin: 0 0 6px;
`;

export const SectionTitle = styled.h2`
	font-size: 1.1rem;
	margin: 20px 0 10px;
`;

export const Muted = styled.p`
	color: ${({ theme }) => theme.colors.textLight};
	font-size: 0.92rem;
	line-height: 1.5;
	margin: 6px 0 10px;
`;

export const ErrorText = styled.p`
	color: ${({ theme }) => theme.colors.error};
	font-size: 0.9rem;
	font-weight: 700;
	margin: 8px 0;
`;

export const Row = styled.div`
	align-items: center;
	display: flex;
	gap: 10px;
	justify-content: space-between;
`;

export const Button = styled.button`
	background: ${({ $plain, $quiet, theme }) => ($plain ? "transparent" : $quiet ? theme.colors.accent : theme.colors.secondary)};
	border: 0;
	border-radius: 10px;
	color: ${({ $plain, $quiet, theme }) => ($plain || $quiet ? theme.colors.primary : "#ffffff")};
	cursor: pointer;
	font: inherit;
	font-weight: 800;
	margin-top: ${({ $wide }) => ($wide ? "14px" : "0")};
	min-height: 44px;
	padding: 0 16px;
	width: ${({ $wide }) => ($wide ? "100%" : "auto")};

	&:disabled {
		cursor: not-allowed;
		opacity: 0.55;
	}
`;

export const ChipRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin: 4px 0 12px;
`;

export const Chip = styled.button`
	background: ${({ $on, theme }) => ($on ? theme.colors.primary : "#ffffff")};
	border: 1px solid ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.gray)};
	border-radius: 999px;
	color: ${({ $on, theme }) => ($on ? "#ffffff" : theme.colors.text)};
	cursor: pointer;
	font: inherit;
	font-size: 0.9rem;
	font-weight: 700;
	min-height: 40px;
	padding: 0 14px;

	&:disabled {
		cursor: default;
		opacity: 0.5;
	}
`;

// Bar tab: the big Beer / Cocktails / Spirits tiles at the top of the menu.
export const TileRow = styled.div`
	display: grid;
	gap: 10px;
	grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
	margin: 4px 0 14px;
`;

export const Tile = styled.button`
	align-items: center;
	background: ${({ $on, theme }) => ($on ? theme.colors.primary : "#ffffff")};
	border: 2px solid ${({ theme }) => theme.colors.primary};
	border-radius: 14px;
	color: ${({ $on, theme }) => ($on ? "#ffffff" : theme.colors.text)};
	cursor: pointer;
	display: flex;
	flex-direction: column;
	font: inherit;
	font-size: 1.05rem;
	font-weight: 800;
	gap: 6px;
	justify-content: center;
	min-height: 104px;
	padding: 12px 6px;

	span[aria-hidden] {
		font-size: 2.2rem;
		line-height: 1;
	}
`;

export const Card = styled.div`
	align-items: center;
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray};
	display: flex;
	gap: 12px;
	justify-content: space-between;
	padding: 12px 0;
`;

export const Thumb = styled.img`
	background: ${({ theme }) => theme.colors.accent};
	border-radius: 10px;
	flex: none;
	height: ${({ $wide }) => ($wide ? "180px" : "64px")};
	object-fit: cover;
	width: ${({ $wide }) => ($wide ? "100%" : "64px")};
`;

export const TotalRow = styled.div`
	display: flex;
	font-size: ${({ $strong }) => ($strong ? "1.05rem" : "0.95rem")};
	font-weight: ${({ $strong }) => ($strong ? 900 : 600)};
	justify-content: space-between;
	padding: 4px 0;
`;

// A panel that slides over the page from the bottom on phones.
export const Sheet = styled.div`
	background: #ffffff;
	border-radius: 16px 16px 0 0;
	bottom: 0;
	box-shadow: 0 -12px 40px rgba(8, 47, 58, 0.2);
	left: 50%;
	max-height: 88vh;
	max-width: 640px;
	overflow-y: auto;
	padding: 18px 16px 24px;
	position: fixed;
	transform: translateX(-50%);
	width: 100%;
	z-index: 20;
`;

// The basket bar pinned to the bottom of the screen.
export const BottomBar = styled.button`
	align-items: center;
	background: ${({ theme }) => theme.colors.primaryDark};
	border: 0;
	border-radius: 14px;
	bottom: 12px;
	color: #ffffff;
	cursor: pointer;
	display: flex;
	font: inherit;
	font-weight: 800;
	justify-content: space-between;
	left: 50%;
	max-width: 608px;
	min-height: 56px;
	padding: 0 18px;
	position: fixed;
	transform: translateX(-50%);
	width: calc(100% - 32px);
	z-index: 10;
`;

export const LiveDot = styled.span`
	background: ${({ $live, theme }) => ($live ? theme.colors.success : theme.colors.gray)};
	border-radius: 50%;
	display: inline-block;
	height: 8px;
	margin-right: 6px;
	width: 8px;
`;

// Which table the guest scanned, big enough to read at a glance.
export const TableBadge = styled.p`
	align-items: center;
	background: ${({ theme }) => theme.colors.accent};
	border-radius: 999px;
	color: ${({ theme }) => theme.colors.primary};
	display: inline-flex;
	font-size: 1.05rem;
	font-weight: 900;
	gap: 6px;
	margin: 4px 0 8px;
	padding: 6px 14px;
`;
